/** Undo/redo history for StudioDraft — same shape as the CDT tree editor's history. */
import { StudioDraft } from "./studioTypes";

const HISTORY_LIMIT = 100;

export interface StudioHistory {
  past: readonly StudioDraft[];
  present: StudioDraft;
  future: readonly StudioDraft[];
}

export const createStudioHistory = (initial: StudioDraft): StudioHistory => ({
  past: [],
  present: initial,
  future: [],
});

export const recordSnapshot = (history: StudioHistory, next: StudioDraft): StudioHistory => ({
  past: [...history.past.slice(-(HISTORY_LIMIT - 1)), history.present],
  present: next,
  future: [],
});

export const undo = (history: StudioHistory): StudioHistory => {
  if (history.past.length === 0) return history;
  return {
    past: history.past.slice(0, -1),
    present: history.past[history.past.length - 1],
    future: [history.present, ...history.future],
  };
};

export const redo = (history: StudioHistory): StudioHistory => {
  if (history.future.length === 0) return history;
  return {
    past: [...history.past, history.present],
    present: history.future[0],
    future: history.future.slice(1),
  };
};

/** A new action voids what Redo holds: a fix adopted on the plan is newer than any draft
 *  step undone before it. */
export const clearRedo = (history: StudioHistory): StudioHistory =>
  history.future.length === 0 ? history : { ...history, future: [] };

/** No merchant edit since inference: every draft from the history's start to the present is
 *  one inference wrote (the base pog's, or the re-inference a wall flip ran). An edit leaves a
 *  draft inference did not write; undoing it moves that draft to `future`, and the history is
 *  untouched again. */
export const isUntouchedSinceInference = (
  history: StudioHistory,
  isInferred: (draft: StudioDraft) => boolean,
): boolean => [...history.past, history.present].every(isInferred);

export const canUndo = (history: StudioHistory) => history.past.length > 0;
export const canRedo = (history: StudioHistory) => history.future.length > 0;
